import { type ReactNode } from 'react'
import { Head, Link } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import CampaignWorkspace from '@/components/CampaignWorkspace'
import { Eye, Ticket, Gavel, Heart, Globe, Share2, CalendarPlus, Bookmark, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { EventAnalytics } from '@/types'

type DashboardEventAnalyticsProps = {
  event: {
    id: string
    slug: string
    title: string
    date: string | null
    status: 'draft' | 'published'
    page_kind: 'event' | 'fundraiser'
  }
  analytics: EventAnalytics
  landing_url: string | null
}

type StatCardProps = {
  label: string
  value: number
  icon: ReactNode
}

const BAR_HEIGHT_CLASSES = [
  'h-[4px]',
  'h-[8px]',
  'h-[12px]',
  'h-[16px]',
  'h-[20px]',
  'h-[24px]',
  'h-[28px]',
  'h-[32px]',
  'h-[36px]',
  'h-[40px]',
  'h-[44px]',
  'h-[48px]',
  'h-[52px]',
  'h-[56px]',
  'h-[60px]',
  'h-[64px]',
  'h-[68px]',
  'h-[72px]',
  'h-[76px]',
  'h-[80px]',
  'h-[84px]',
  'h-[88px]',
  'h-[92px]',
  'h-[96px]',
  'h-[100px]',
  'h-[104px]',
  'h-[108px]',
  'h-[112px]',
  'h-[116px]',
  'h-[120px]',
  'h-[124px]',
  'h-[128px]',
  'h-[132px]',
  'h-[136px]',
  'h-[140px]',
] as const

function barHeightClass(count: number, maxValue: number) {
  const ratio = maxValue === 0 ? 0 : count / maxValue
  const index = Math.max(Math.ceil(ratio * BAR_HEIGHT_CLASSES.length) - 1, 0)

  return BAR_HEIGHT_CLASSES[index]
}

function StatCard({ label, value, icon }: StatCardProps) {
  return (
    <div className="rounded-xl border border-black/10 bg-card p-4 shadow-xs">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-base font-medium">{label}</span>
      </div>
      <p className="mt-2 text-3xl font-semibold tabular-nums">{value.toLocaleString()}</p>
    </div>
  )
}

function DailyViewsChart({ dailyViews }: { dailyViews: Record<string, number> }) {
  const entries = Object.entries(dailyViews).sort(([a], [b]) => a.localeCompare(b))

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-black/10 p-8 text-center text-base text-muted-foreground">
        No view data yet
      </div>
    )
  }

  const maxValue = Math.max(...entries.map(([, v]) => v), 1)

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Views — Last 30 Days</h2>
      <div className="flex h-40 items-end gap-1">
        {entries.map(([date, count]) => {
          const shortDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
          return (
            <div key={date} className="group relative flex flex-1 flex-col items-center justify-end">
              <div className="absolute -top-6 hidden rounded bg-foreground px-1.5 py-0.5 text-sm text-background group-hover:block">
                {count} — {shortDate}
              </div>
              <div
                className={`w-full rounded-t bg-primary/70 transition-colors group-hover:bg-primary ${barHeightClass(count, maxValue)}`}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function DashboardEventAnalytics({ event, analytics, landing_url }: DashboardEventAnalyticsProps) {
  const isFundraiser = event.page_kind === 'fundraiser'

  return (
    <>
      <Head title={`Analytics — ${event.title}`} />
      <CampaignWorkspace
        event={{ ...event, landing_url }}
        title="Analytics"
        active="analytics"
        actions={(
          <>
            {event.status === 'published' && (
              <Button variant="outline" size="sm" asChild>
                <a href={`/events/${event.slug}`} target="_blank" rel="noopener noreferrer">View</a>
              </Button>
            )}
            {event.status === 'published' && !isFundraiser && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/dashboard/events/${event.id}/check_ins`}>Check in</Link>
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link href={`/dashboard/events/${event.id}/edit`}>Edit</Link>
            </Button>
          </>
        )}
      >
        <div className="max-w-5xl">

        {/* Stat Cards */}
        <div className={`mb-8 grid grid-cols-2 gap-4 ${isFundraiser ? 'sm:grid-cols-3' : 'sm:grid-cols-3 lg:grid-cols-5'}`}>
          <StatCard label="Page Views" value={analytics.page_views} icon={<Eye className="size-4" />} />
          {!isFundraiser && <StatCard label="Ticket Clicks" value={analytics.ticket_clicks} icon={<Ticket className="size-4" />} />}
          {!isFundraiser && <StatCard label="Auction Clicks" value={analytics.auction_clicks} icon={<Gavel className="size-4" />} />}
          <StatCard label="Donation Clicks" value={analytics.donation_clicks} icon={<Heart className="size-4" />} />
          {!isFundraiser && <StatCard label="Raffle Clicks" value={analytics.raffle_clicks} icon={<Ticket className="size-4" />} />}
          {!isFundraiser && <StatCard label="Sponsor Clicks" value={analytics.sponsor_clicks} icon={<Globe className="size-4" />} />}
          <StatCard label="Shares" value={analytics.share_clicks} icon={<Share2 className="size-4" />} />
          {!isFundraiser && <StatCard label="Calendar Saves" value={analytics.calendar_saves} icon={<CalendarPlus className="size-4" />} />}
          {!isFundraiser && <StatCard label="RSVPs" value={analytics.attendee_count} icon={<Users className="size-4" />} />}
          {!isFundraiser && <StatCard label="Saves" value={analytics.saved_count} icon={<Bookmark className="size-4" />} />}
        </div>

        {/* Daily Views Chart */}
        <DailyViewsChart dailyViews={analytics.daily_views} />
        </div>
      </CampaignWorkspace>
    </>
  )
}

DashboardEventAnalytics.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
